import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { dashboardAPI } from '../lib/api';
import { formatCurrency, formatDate, getStatusColor, getStatusLabel } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Users,
  FileText,
  Briefcase,
  TrendingUp,
  Plus,
  ArrowRight,
  Euro,
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const COLORS = ['#64748b', '#f97316', '#10b981', '#ef4444'];

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await dashboardAPI.stats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const chartData = stats ? [
    { name: 'Bozza', value: stats.preventivi_per_stato.bozza },
    { name: 'Inviato', value: stats.preventivi_per_stato.inviato },
    { name: 'Accettato', value: stats.preventivi_per_stato.accettato },
    { name: 'Rifiutato', value: stats.preventivi_per_stato.rifiutato },
  ].filter(item => item.value > 0) : [];

  if (loading) {
    return (
      <Layout>
        <div className="space-y-8">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">Dashboard</h1>
            <p className="text-slate-500 mt-1">Panoramica della tua attività</p>
          </div>
          <Link to="/preventivi/nuovo">
            <Button data-testid="new-preventivo-btn" className="bg-brand-orange hover:bg-brand-orange-dark text-white">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Preventivo
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-slate-200 stats-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Clienti</p>
                  <p className="text-3xl font-heading font-bold text-slate-900 mt-1">
                    {stats?.clienti || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 stats-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Preventivi</p>
                  <p className="text-3xl font-heading font-bold text-slate-900 mt-1">
                    {stats?.preventivi || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-brand-orange" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 stats-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Servizi</p>
                  <p className="text-3xl font-heading font-bold text-slate-900 mt-1">
                    {stats?.servizi || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 stats-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Valore Totale</p>
                  <p className="text-2xl font-heading font-bold text-slate-900 mt-1">
                    {formatCurrency(stats?.totale_scontato || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Euro className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts and Recent */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chart */}
          <Card className="border-slate-200 lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Stato Preventivi</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap justify-center gap-4 mt-4">
                    {chartData.map((entry, index) => (
                      <div key={entry.name} className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-sm text-slate-600">
                          {entry.name}: {entry.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-500">
                  Nessun preventivo ancora
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Preventivi */}
          <Card className="border-slate-200 lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-heading">Ultimi Preventivi</CardTitle>
              <Link to="/preventivi">
                <Button variant="ghost" size="sm" className="text-slate-600">
                  Vedi tutti
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {stats?.recenti?.length > 0 ? (
                <div className="space-y-4">
                  {stats.recenti.map((preventivo) => (
                    <Link
                      key={preventivo.id}
                      to={`/preventivi/${preventivo.id}`}
                      className="block"
                    >
                      <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <p className="font-medium text-slate-900 truncate">
                              {preventivo.codice}
                            </p>
                            <Badge
                              variant="outline"
                              className={getStatusColor(preventivo.stato)}
                            >
                              {getStatusLabel(preventivo.stato)}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500 mt-1 truncate">
                            {preventivo.cliente_ragione_sociale}
                          </p>
                        </div>
                        <div className="text-right ml-4">
                          <p className="font-medium text-slate-900">
                            {formatCurrency(preventivo.totale_scontato)}
                          </p>
                          <p className="text-sm text-slate-500">
                            {formatDate(preventivo.data)}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">Nessun preventivo ancora</p>
                  <Link to="/preventivi/nuovo">
                    <Button variant="outline">
                      <Plus className="w-4 h-4 mr-2" />
                      Crea il primo preventivo
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
